import { SingleValueWidgetGroup } from 'src/components/fintech-ops/shared/utils/cloudWatchDashboardMetrics';

/**
 * Prompt + tool schema for the dashboard-agnostic AI narrative shown on the KPI Metrics
 * page. Unlike the OE Report §5 prompts (which know they are describing API / Database /
 * User Activity rows), this one makes no assumption about what a dashboard measures — it
 * is handed the resolved `singleValue` widget numbers for one dashboard over one month
 * and asked to summarize them. That keeps it reusable across every dashboard tab.
 */

/** One widget's numbers as sent to the model. */
export interface DashboardNarrativeWidget {
  /** The widget's title, e.g. "API Availability". */
  title: string;
  /** Resolved metric lines: label plus both aggregates so the model can pick what fits. */
  entries: { label: string; sum: number; avg: number }[];
}

/** The full context sent to the model for one dashboard's narrative. */
export interface DashboardNarrativePromptData {
  /** Human-readable dashboard name (the tab label), e.g. "Aggregated API Health". */
  dashboardName: string;
  /** The reporting window the numbers cover, e.g. "September 2026". */
  month: string;
  /** Resolved singleValue widgets for the dashboard. */
  widgets: DashboardNarrativeWidget[];
}

/** Response shape enforced by the model via tool-use. */
export interface DashboardNarrativeFields {
  /** The summary paragraph. */
  summary?: string;
}

/** Round to at most 2 decimals so the prompt stays compact and readable. */
const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Maps extracted widget groups to the compact prompt shape. Kept separate so the hook
 * can build the model input without duplicating the sum/avg selection.
 */
export const toNarrativeWidgets = (groups: SingleValueWidgetGroup[]): DashboardNarrativeWidget[] =>
  groups.map((g) => ({
    title: g.title,
    entries: g.entries.map((e) => ({ label: e.label, sum: round2(e.sum), avg: round2(e.avg) }))
  }));

/** Serializes the widget numbers into the data block shared by the prompt. */
const buildDataBlock = (data: DashboardNarrativePromptData): string => `Data:
- Dashboard: ${data.dashboardName}
- Reporting window: ${data.month}
- Widgets (each has a title and one or more metric lines; "sum" is the total across the window, "avg" is the mean): ${JSON.stringify(data.widgets)}

Each metric line is one value on the dashboard. Use "sum" for count-like metrics (totals,
request counts, error counts) and "avg" for rates, percentages, and latencies. Values are
already aggregated over the reporting window.`;

/**
 * Default prompt for the dashboard narrative. No reviewer-instruction mode in v1 — the
 * KPI Metrics page offers regenerate only.
 */
export const buildDashboardNarrativePrompt = (
  data: DashboardNarrativePromptData
): string => `You are summarizing a CloudWatch operational dashboard for an engineering audience.

${buildDataBlock(data)}

Write 3-5 sentences that summarize the dashboard's current state for ${data.month}. Open
with the single most important number or signal, then cover the notable metrics and any
values that look concerning (very high error/latency figures, low availability, etc.).
Ground every statement in the numbers above — do not invent metrics, thresholds, or
trends the data does not show. If the data is sparse, say so briefly rather than padding.
Plain prose, no bullet points, no markdown.`;

/**
 * Tool schema for the narrative — a single summary string, enforced by the model so the
 * response never needs to be parsed out of free text.
 */
export const DASHBOARD_NARRATIVE_SCHEMA = {
  name: 'dashboard_narrative',
  description: 'A concise natural-language summary of a CloudWatch dashboard for the reporting window.',
  json: {
    type: 'object',
    properties: {
      summary: { type: 'string', description: '3-5 sentences summarizing the dashboard, grounded in the provided numbers' }
    },
    required: ['summary']
  }
};

/** Render the model's fields into the narrative shown in the UI. */
export const renderDashboardNarrative = (fields: DashboardNarrativeFields): string => (fields.summary ?? '').trim();
